import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useOverview } from '@/hooks/useIssueMemory';
import { AiHelp, OverviewIssue } from '@/api/issue-memory.api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Bot, CheckCircle2, CircleDot, ExternalLink, GitMerge, ListChecks, RotateCcw, Sparkles } from 'lucide-react';
import { cn, formatDate } from '@/utils/helpers';

/**
 * Overview: what the team did in the last days (owner, 2026-10-27): issues worked, opened and closed, how many had
 * AI help (the AI developer, AI QA and review, or a person's own Claude Code), merged MRs and closes the DoD check
 * turned back, per project and per person. Counts come from the issue memory, so only followed projects count.
 */

const RANGES = [7, 14, 30];

const AI_TONE: Record<AiHelp, string> = {
  'AI developer': 'bg-violet-100 text-violet-800',
  'AI QA': 'bg-amber-100 text-amber-800',
  'AI review': 'bg-sky-100 text-sky-800',
  'Claude Code (person)': 'bg-slate-100 text-slate-700',
};

type Filter = 'all' | 'closed' | 'ai';

function projectName(path: string | undefined, id: number): string {
  return path?.split('/').pop() || `project ${id}`;
}

function percent(part: number, whole: number): string {
  return whole ? `${Math.round((part / whole) * 100)}%` : '–';
}

function Stat({ title, value, note, icon: Icon }: { title: string; value: number | string; note?: string; icon: React.ComponentType<{ className?: string }> }) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        {note && <p className="text-xs text-muted-foreground">{note}</p>}
      </CardContent>
    </Card>
  );
}

function IssueRow({ issue }: { issue: OverviewIssue }) {
  return (
    <tr className="border-b align-top hover:bg-muted/50">
      <td className="py-2 px-2 whitespace-nowrap">
        <Link to={`/issues/${issue.gitlabProjectId}/${issue.iid}`} className="font-mono text-xs text-primary hover:underline">
          {issue.ref}
        </Link>
      </td>
      <td className="py-2 px-2">
        <span dir="auto" className="text-sm">
          {issue.title}
        </span>
        {issue.webUrl && (
          <a href={issue.webUrl} target="_blank" rel="noreferrer" className="ml-1 inline-block text-muted-foreground hover:text-foreground">
            <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </td>
      <td className="py-2 px-2 text-xs whitespace-nowrap">
        {issue.closed ? (
          <span className="text-green-700">closed {issue.closedAt ? formatDate(issue.closedAt) : ''}</span>
        ) : issue.state === 'closed' ? (
          'closed'
        ) : (
          <span>
            {issue.status}
            {issue.opened ? ' · new' : ''}
          </span>
        )}
      </td>
      <td className="py-2 px-2 text-xs">{issue.assignees.length ? issue.assignees.map((a) => `@${a}`).join(', ') : 'unassigned'}</td>
      <td className="py-2 px-2">
        <span className="flex flex-wrap gap-1">
          {issue.ai.map((kind) => (
            <span key={kind} className={cn('rounded px-1.5 py-0.5 text-[10px] uppercase tracking-wide', AI_TONE[kind])}>
              {kind}
            </span>
          ))}
          {!issue.ai.length && issue.closed && issue.aiEver && (
            <span className="rounded bg-violet-50 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-violet-700">AI earlier</span>
          )}
          {issue.dodReopens > 0 && (
            <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-amber-800">
              reopened by DoD ×{issue.dodReopens}
            </span>
          )}
        </span>
      </td>
    </tr>
  );
}

export default function Overview() {
  const [days, setDays] = useState(7);
  const [filter, setFilter] = useState<Filter>('all');
  const { data, isLoading, error } = useOverview(days);

  const aiKinds = useMemo(() => {
    const counts = new Map<AiHelp, number>();
    for (const issue of data?.issues || []) for (const kind of issue.ai) counts.set(kind, (counts.get(kind) || 0) + 1);
    return [...counts.entries()].sort((a, b) => b[1] - a[1]);
  }, [data]);

  const issues = useMemo(
    () =>
      (data?.issues || []).filter((i) => (filter === 'closed' ? i.closed : filter === 'ai' ? i.ai.length > 0 || (i.closed && i.aiEver) : true)),
    [data, filter]
  );

  if (isLoading) return <LoadingSpinner className="py-24" />;
  if (error || !data) return <p className="text-sm text-red-600">Could not load the overview: {(error as Error)?.message}</p>;

  const { totals } = data;
  const sessionsNote = [
    ...Object.entries(data.aiSessions.byRole).map(([role, n]) => `${n} ${role}`),
    `${totals.aiTurns} turns`,
    ...(totals.aiCostUsd !== undefined ? [`$${totals.aiCostUsd.toFixed(2)} API cost`] : []),
  ].join(' · ');

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Overview</h1>
          <p className="text-sm text-muted-foreground">
            {formatDate(data.since)} – {formatDate(data.until)} · projects the AI manager follows
          </p>
        </div>
        <div className="flex gap-2">
          {RANGES.map((n) => (
            <Button key={n} size="sm" variant={n === days ? 'default' : 'outline'} onClick={() => setDays(n)}>
              Last {n} days
            </Button>
          ))}
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <Stat title="Issues worked on" value={totals.worked} note={`${totals.opened} opened in the period`} icon={ListChecks} />
        <Stat
          title="Closed"
          value={totals.closed}
          note={`${totals.closedWithAi} with AI help (${percent(totals.closedWithAi, totals.closed)})`}
          icon={CheckCircle2}
        />
        <Stat title="Worked on with AI" value={totals.withAi} note={`${percent(totals.withAi, totals.worked)} of the issues worked on`} icon={Sparkles} />
        <Stat title="AI sessions" value={totals.aiSessions} note={sessionsNote} icon={Bot} />
        <Stat title="MRs merged" value={totals.mergedMrs} note="linked to an issue" icon={GitMerge} />
        <Stat title="Reopened by the DoD check" value={totals.dodReopens} note="closes turned back: evidence or MR missing" icon={RotateCcw} />
        <Stat title="Opened" value={totals.opened} icon={CircleDot} />
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">AI help by kind</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 text-sm">
            {aiKinds.length ? (
              aiKinds.map(([kind, n]) => (
                <div key={kind} className="flex justify-between">
                  <span className="text-muted-foreground">{kind}</span>
                  <span className="font-medium">{n}</span>
                </div>
              ))
            ) : (
              <p className="text-muted-foreground">No AI sessions in the period</p>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>By project</CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left">
                  <th className="py-2 px-2 font-medium">Project</th>
                  <th className="py-2 px-2 text-center font-medium">Worked on</th>
                  <th className="py-2 px-2 text-center font-medium">Opened</th>
                  <th className="py-2 px-2 text-center font-medium">Closed</th>
                  <th className="py-2 px-2 text-center font-medium">Closed with AI</th>
                  <th className="py-2 px-2 text-center font-medium">MRs merged</th>
                  <th className="py-2 px-2 text-center font-medium">DoD reopens</th>
                </tr>
              </thead>
              <tbody>
                {data.projects.map((p) => (
                  <tr key={p.gitlabProjectId} className="border-b">
                    <td className="py-2 px-2 font-medium">{projectName(p.projectPath, p.gitlabProjectId)}</td>
                    <td className="py-2 px-2 text-center">{p.worked}</td>
                    <td className="py-2 px-2 text-center">{p.opened}</td>
                    <td className="py-2 px-2 text-center">{p.closed}</td>
                    <td className="py-2 px-2 text-center">{p.closedWithAi}</td>
                    <td className="py-2 px-2 text-center">{p.mergedMrs}</td>
                    <td className="py-2 px-2 text-center">{p.dodReopens || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>By assignee</CardTitle>
            <CardDescription>Issues worked on and closed, by who they are assigned to</CardDescription>
          </CardHeader>
          <CardContent>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left">
                  <th className="py-2 px-2 font-medium">Person</th>
                  <th className="py-2 px-2 text-center font-medium">Worked on</th>
                  <th className="py-2 px-2 text-center font-medium">Closed</th>
                </tr>
              </thead>
              <tbody>
                {data.people.map((p) => (
                  <tr key={p.username} className="border-b">
                    <td className="py-2 px-2">
                      @{p.username}
                      {p.ai && <span className="ml-1 rounded bg-violet-100 px-1 text-[10px] uppercase text-violet-800">AI</span>}
                    </td>
                    <td className="py-2 px-2 text-center">{p.worked}</td>
                    <td className="py-2 px-2 text-center">{p.closed}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 space-y-0">
          <CardTitle>Issues</CardTitle>
          <div className="flex gap-2">
            {(
              [
                ['all', `All (${data.issues.length})`],
                ['closed', `Closed (${totals.closed})`],
                ['ai', 'With AI help'],
              ] as Array<[Filter, string]>
            ).map(([key, label]) => (
              <Button key={key} size="sm" variant={filter === key ? 'default' : 'outline'} onClick={() => setFilter(key)}>
                {label}
              </Button>
            ))}
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {issues.length ? (
            <table className="w-full">
              <thead>
                <tr className="border-b text-left text-sm">
                  <th className="py-2 px-2 font-medium">Issue</th>
                  <th className="py-2 px-2 font-medium">Title</th>
                  <th className="py-2 px-2 font-medium">State</th>
                  <th className="py-2 px-2 font-medium">Assignee</th>
                  <th className="py-2 px-2 font-medium">AI help · notes</th>
                </tr>
              </thead>
              <tbody>
                {issues.map((issue) => (
                  <IssueRow key={`${issue.gitlabProjectId}#${issue.iid}`} issue={issue} />
                ))}
              </tbody>
            </table>
          ) : (
            <p className="py-8 text-center text-sm text-muted-foreground">No issues</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
